import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { NAV } from "../Navbar/navbarTheme";
import { sectionTitleSpacingSx } from "../sectionHeaderStyles";
import integrationImg from "../Images/Brands/integration.png";
import { fetchAllBrands, getBrandProductsPath } from "../utils/shopApi";

const PAGE_MAX_WIDTH = 980;

const brandSx = {
  navy: NAV.navy,
  blue: "#1e5a9e",
  muted: NAV.inkMuted,
  ink: NAV.ink,
  blueLight: "#eef5fb",
  blueBorder: "#c5d9ed",
  line: NAV.line,
};

const getBrandInitial = (name) => {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "#";
  const first = trimmed.charAt(0).toUpperCase();
  return /[A-Z]/.test(first) ? first : "#";
};

const groupBrandsByLetter = (brands) => {
  const groups = {};

  brands.forEach((brand) => {
    const letter = getBrandInitial(brand?.name);
    if (!groups[letter]) groups[letter] = [];
    groups[letter].push(brand);
  });

  return Object.keys(groups)
    .sort((a, b) => {
      if (a === "#") return 1;
      if (b === "#") return -1;
      return a.localeCompare(b);
    })
    .map((letter) => ({
      letter,
      brands: groups[letter].sort((a, b) =>
        (a?.name ?? "").localeCompare(b?.name ?? "", undefined, {
          sensitivity: "base",
        }),
      ),
    }));
};

const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    const loadBrands = async () => {
      try {
        setLoading(true);
        const data = await fetchAllBrands();

        if (isMounted) {
          setBrands(data);
          setError("");
        }
      } catch {
        if (isMounted) {
          setBrands([]);
          setError("Unable to load brands right now.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadBrands();
    return () => {
      isMounted = false;
    };
  }, []);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredBrands = useMemo(
    () =>
      brands
        .filter((brand) =>
          (brand?.name ?? "").toLowerCase().includes(normalizedQuery),
        )
        .sort((a, b) =>
          (a?.name ?? "").localeCompare(b?.name ?? "", undefined, {
            sensitivity: "base",
          }),
        ),
    [brands, normalizedQuery],
  );

  const brandGroups = useMemo(
    () => groupBrandsByLetter(filteredBrands),
    [filteredBrands],
  );

  return (
    <Box
      sx={{
        pt: "clamp(96px, 12vh, 140px)",
        pb: { xs: 5, md: 7 },
        px: { xs: 2, sm: 3 },
        bgcolor: "#fff",
      }}
    >
      <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: "auto" }}>
        <Stack spacing={2} sx={{ mb: { xs: 3, md: 4 }, textAlign: "center" }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              ...sectionTitleSpacingSx,
              color: brandSx.navy,
              fontSize: { xs: "1.65rem", md: "2.125rem" },
              lineHeight: 1.25,
            }}
          >
            Trusted Wholesale FMCG Brands
          </Typography>
          <Typography
            sx={{
              color: brandSx.ink,
              fontSize: { xs: 15, md: 16 },
              lineHeight: 1.65,
              maxWidth: 860,
              mx: "auto",
            }}
          >
            UK TO MARS LTD supplies a wide range of British and international
            FMCG brands across food, drinks, confectionery, grocery, household,
            personal care, baby care, frozen, pet care, and specialist
            categories.
          </Typography>
        </Stack>

        <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
          <TextField
            fullWidth
            placeholder="Search Brands"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            sx={{
              maxWidth: 480,
              "& .MuiOutlinedInput-root": {
                borderRadius: 999,
                backgroundColor: "#ffffff",
                boxShadow: "0 4px 18px rgba(15, 41, 71, 0.08)",
                border: `1px solid ${brandSx.line}`,
                transition: "box-shadow 0.2s, border-color 0.2s",
                "&:hover": {
                  borderColor: brandSx.blueBorder,
                },
                "&.Mui-focused": {
                  boxShadow: "0 6px 22px rgba(23, 61, 102, 0.14)",
                  borderColor: "#94a3b8",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: brandSx.muted }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            mb: { xs: 3, md: 4 },
            p: { xs: 2, md: 2.5 },
            borderRadius: 2.5,
            bgcolor: brandSx.blueLight,
            border: `1px solid ${brandSx.blueBorder}`,
          }}
        >
          <Typography
            sx={{
              color: brandSx.ink,
              fontSize: { xs: 14.5, md: 15.5 },
              lineHeight: 1.65,
              textAlign: { xs: "left", md: "center" },
            }}
          >
            Can&apos;t find the brand you&apos;re looking for? Our supplier
            network gives us access to many additional UK and European FMCG
            brands beyond those listed.{" "}
            <Link
              component={RouterLink}
              to="/ContactUs"
              sx={{
                color: brandSx.blue,
                fontWeight: 700,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Contact us
            </Link>{" "}
            with your requirements and our team will confirm availability and
            pricing.
          </Typography>
        </Box>

        {error ? (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid #fecaca",
              bgcolor: "#fff5f5",
            }}
          >
            <Typography color="error" sx={{ fontWeight: 600 }}>
              {error}
            </Typography>
          </Box>
        ) : null}

        {loading ? (
          <Typography
            sx={{
              textAlign: "center",
              color: brandSx.muted,
              fontWeight: 500,
              py: 4,
            }}
          >
            Loading brands...
          </Typography>
        ) : null}

        {!loading && !error && brandGroups.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
                lg: "repeat(5, minmax(0, 1fr))",
              },
              gap: { xs: 2.5, md: 3 },
              mb: { xs: 4, md: 5 },
            }}
          >
            {brandGroups.map(({ letter, brands: letterBrands }) => (
              <Box key={letter}>
                <Typography
                  component="h2"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.35rem", md: "1.5rem" },
                    color: brandSx.navy,
                    mb: 1,
                    pb: 0.75,
                    borderBottom: `2px solid ${brandSx.blueBorder}`,
                  }}
                >
                  {letter}
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.25,
                    listStyleType: "disc",
                    color: brandSx.blue,
                  }}
                >
                  {letterBrands.map((brand) => (
                    <Box
                      component="li"
                      key={brand.id ?? brand.slug ?? brand.name}
                      sx={{
                        mb: 0.35,
                        color: brandSx.ink,
                        "&::marker": { color: brandSx.blue },
                      }}
                    >
                      <Link
                        component={RouterLink}
                        to={getBrandProductsPath(brand)}
                        state={{ brand }}
                        underline="hover"
                        sx={{
                          color: brandSx.ink,
                          fontSize: { xs: 13.5, md: 14.5 },
                          fontWeight: 500,
                          textDecoration: "none",
                          "&:hover": {
                            color: brandSx.blue,
                          },
                        }}
                      >
                        {brand?.name ?? "Unnamed brand"}
                      </Link>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : null}

        {!loading && !error && filteredBrands.length === 0 ? (
          <Box
            sx={{
              mb: 4,
              p: 3,
              borderRadius: 2.5,
              textAlign: "center",
              border: `1px dashed ${brandSx.blueBorder}`,
              bgcolor: brandSx.blueLight,
            }}
          >
            <Typography sx={{ color: brandSx.muted, fontWeight: 500 }}>
              No brands match your search. Try a different name.
            </Typography>
          </Box>
        ) : null}

        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            pt: { xs: 2.5, md: 3 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 2.5, md: 4 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.2rem", md: "1.35rem" },
                color: brandSx.ink,
                mb: 1.25,
              }}
            >
              Become a Brand Partner?
            </Typography>
            <Typography
              sx={{
                color: brandSx.ink,
                fontSize: { xs: 14.5, md: 15.5 },
                lineHeight: 1.65,
              }}
            >
              We&apos;re always looking to add new FMCG brands to our portfolio.
              If you&apos;re a manufacturer, brand owner, or supplier looking to
              grow across UK wholesale and export markets, please get in touch.{" "}
              <Link
                component={RouterLink}
                to="/ContactUs"
                sx={{
                  color: brandSx.blue,
                  fontWeight: 700,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Contact Us
              </Link>
            </Typography>
          </Box>
          <Box
            component="img"
            src={integrationImg}
            alt="Global brand partnership and wholesale integration"
            sx={{
              width: { xs: 100, sm: 120, md: 130 },
              height: "auto",
              flexShrink: 0,
              display: "block",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BrandsPage;
